import React, { useMemo, useState, useEffect } from 'react';
import classNames from 'classnames';
import { format } from 'date-fns';
import { AsyncDataHandler, DatePicker, Input, Select } from '@design-system';
import { IOption, SelectChangeVariant } from '@design-system/elements/Select/Select.types';
import { InputChangeVariant } from '@ds/elements/Input/Input.types';
import { DatePickerChangeVariant } from '@design-system/elements/DatePicker/DatePicker.types';
import { DefaultDateFormat } from 'src/utils/constants/common';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { useGetDependents } from 'src/shared/queries/useGetDependents';
import { useGetDropdownItems } from 'src/shared/queries/useGetDropdownItems';
import { useGetReferenceData } from 'src/shared/queries/useGetReferenceData';
import { useGetCountries } from 'src/shared/queries/useGetCountries';
import { useGetStates } from 'src/shared/queries/useGetStates';
import { useGetBeneficiary } from 'src/shared/queries/useGetBeneficiary';
import NationalityList, { INationalityProps } from './NationalityList/NationalityList';
import { useUserProfileStore } from 'src/stores/UserProfile';
import { IProfileState, InitialProfileState } from './BasicInformation.service';
import { IBeneficiary, IDependent, INationality } from 'src/shared/types/Beneficiary';
import { GetBeneficiaryModifiedFields } from '../../UserProfile.service';
import styles from './BasicInformation.module.scss';

const Fields = [
  'prefix',
  'firstName',
  'middleName',
  'lastName',
  'relationship',
  'preferredName',
  'otherName',
  'gender',
  'maritalStatus',
  'dob',
  'cob',
  'stateProvinceOfBirth',
  'cityOfBirth',
];

const BasicInformation: React.FC = () => {
  const {
    editMode,
    displayUpdateButton,
    profileInfo: selectedProfile,
    enableSaveChanges,
    setEnableSaveChanges,
    setDisplayUpdateButton,
    setUpdatedFields,
  } = useUserProfileStore();
  const [profileState, setProfileState] = useState<IProfileState>(InitialProfileState);
  const [memberInfo, setMemberInfo] = useState<IBeneficiary | IDependent | undefined>(undefined);
  const { data: userInfo } = useGetUser();
  const { data: dependents } = useGetDependents(userInfo?.beneId ?? 0);
  const { data: beneInfo } = useGetBeneficiary(userInfo?.beneId === selectedProfile?.id ? (userInfo?.beneId ?? 0) : 0);
  const { data: prefixes, isFetched: isFetchedPrefix } = useGetDropdownItems('PREFIX');
  const { data: genders, isFetched: isFetchedGenders } = useGetReferenceData('GENDER');
  const { data: maritalStatuses, isFetched: isFetchedMaritalStatues } = useGetReferenceData('MARITAL_STATUS');
  const { data: relationships, isFetched: isFetchedRelatioships } = useGetReferenceData('DEPENDENT_RELATIONSHIP');
  const { data: countries, isFetched: isFetchedCountries } = useGetCountries();
  const { data: states, isFetched: isFetchedStates } = useGetStates(profileState?.cob?.value as string);
  const isNewDependent = selectedProfile?.id === -1;
  const dateFormat = userInfo?.userSettings?.dateFormat;
  const prefixOptions = useMemo(() => prefixes?.map(({ type }) => ({ label: type, value: type })) || [], [prefixes]);

  const genderOptions = useMemo(
    () => genders?.map(({ code, description }) => ({ label: description, value: code })) || [],
    [genders]
  );

  const maritalStatusOptions = useMemo(
    () => maritalStatuses?.map(({ code, description }) => ({ label: description, value: code })) || [],
    [maritalStatuses]
  );

  const relationshipOptions = useMemo(
    () => relationships?.map(({ code, description }) => ({ label: description, value: code })) || [],
    [relationships]
  );

  const countryOptions = useMemo(
    () =>
      countries?.map(({ countryCode, countryName }) => ({
        label: countryName,
        value: countryCode,
      })) || [],
    [countries]
  );

  const stateOptions = useMemo(
    () => states?.map(({ stateName }) => ({ label: stateName, value: stateName })) || [],
    [states]
  );

  useEffect(() => {
    setDisplayUpdateButton(false);
  }, []);

  useEffect(() => {
    const memberInfo = getMemberInfo();

    if (!memberInfo) return;

    setMemberInfo(memberInfo);
    setDisplayUpdateButton(true);
  }, [beneInfo, dependents, selectedProfile]);

  useEffect(() => {
    if (!memberInfo) return;

    const nationalities = memberInfo.nationalities.map((item) => {
      let country = item;
      if (typeof item === 'string') {
        country = countries?.find((country) => country.countryCode === item) as INationality;
      }
      return { label: country.countryName, value: country.countryCode, isNew: false };
    });

    setProfileState({
      ...profileState,
      firstName: memberInfo.firstName,
      middleName: memberInfo.middleName,
      lastName: memberInfo.lastName,
      preferredName: memberInfo.preferredName,
      otherName: memberInfo.otherName,
      dob: memberInfo.dob,
      cityOfBirth: memberInfo.cityOfBirth,
      stateProvinceOfBirth: memberInfo.stateProvinceOfBirth,
      nationalities,
      relationship: { label: memberInfo.relationship, value: memberInfo.relationship },
    });
  }, [memberInfo]);

  useEffect(() => {
    if (!memberInfo) return;

    const findAndSetOption = <T extends Record<string, string | number>>(
      field: keyof IProfileState,
      collection: T[] | undefined,
      lookupKey: keyof T | string,
      profileValue: string | undefined,
      valueKey: keyof T | string = 'code',
      labelKey: keyof T | string = 'code'
    ) => {
      if (!profileValue || !collection || collection.length === 0) {
        setProfileState((prevState) => ({ ...prevState, [field]: null }));
        return;
      }

      const option = collection.find((item) => item[lookupKey as keyof T] === profileValue);
      if (option) {
        const value = {
          label: option[labelKey as keyof T] as string,
          value: option[valueKey as keyof T] as string,
        };
        setProfileState((prevState) => ({ ...prevState, [field]: value }));
      }
    };

    const getList = (items: unknown) => items as unknown as Record<string, string | number>[];
    findAndSetOption('prefix', getList(prefixes), 'type', memberInfo.prefix, 'type', 'type');
    findAndSetOption('gender', getList(genders), 'code', memberInfo.gender);
    findAndSetOption('cob', getList(countries), 'countryCode', memberInfo.cob, 'countryCode', 'countryCode');
    findAndSetOption('relationship', getList(relationships), 'code', memberInfo.relationship);
    findAndSetOption('maritalStatus', getList(maritalStatuses), 'code', memberInfo.maritalStatus);
  }, [memberInfo, prefixes, genders, countries, relationships, maritalStatuses]);

  useEffect(() => {
    if (editMode && memberInfo && profileState) {
      const modifiedFields = GetBeneficiaryModifiedFields(memberInfo, profileState, Fields);
      const updatedNationalities = profileState.nationalities?.map((item) => item.value) || [];
      const originalNationalities = memberInfo.nationalities?.map((item) => item.countryCode) || [];
      const isNationalitiesEqual =
        updatedNationalities?.length === originalNationalities?.length &&
        updatedNationalities?.every((v, i) => v === originalNationalities[i]);
      if (!isNationalitiesEqual) {
        modifiedFields.nationalities = updatedNationalities;
      }
      const hasChanged = Object.keys(modifiedFields).length > 0;
      if (hasChanged && !modifiedFields.beneId) {
        modifiedFields.beneId = memberInfo.beneId;
      } else {
        delete modifiedFields.beneId;
      }

      setUpdatedFields(
        Object.keys(modifiedFields).length
          ? { basicInformation: { ...modifiedFields, ...(isNewDependent && { isNew: true }) } }
          : null
      );
    }
  }, [editMode, profileState, memberInfo]);

  useEffect(() => {
    if (
      isFetchedPrefix &&
      isFetchedGenders &&
      isFetchedMaritalStatues &&
      isFetchedRelatioships &&
      isFetchedCountries &&
      isFetchedStates
    ) {
      setDisplayUpdateButton(true);
    }
  }, [
    isFetchedPrefix,
    isFetchedGenders,
    isFetchedMaritalStatues,
    isFetchedRelatioships,
    isFetchedCountries,
    isFetchedStates,
  ]);

  const getMemberInfo = () => {
    let memberInfo: IBeneficiary | IDependent | undefined = beneInfo;

    if (selectedProfile && !selectedProfile.isPrincipal) {
      if (isNewDependent) {
        memberInfo = InitialProfileState as unknown as IDependent;
      } else if (dependents && dependents.length > 0) {
        memberInfo = dependents.find((dependent) => dependent.dependentId === selectedProfile.id);
      }
    }

    return memberInfo;
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileState((prevState) => ({ ...prevState, [field]: event.target.value }));
  };

  const handleDropdownChange = (field: string) => (data: IOption | null) => {
    setProfileState((prevState) => ({ ...prevState, [field]: data }));
  };

  const handleDateChange = (value: Date | null) => {
    const newDate = value ? format(value, DefaultDateFormat) : '';
    setProfileState((prevState) => ({ ...prevState, dob: newDate }));
  };

  const handleNationalitiesUpdate = (data: INationalityProps[]) => {
    setEnableSaveChanges(enableSaveChanges || data.some((item) => item.isNew));
    setProfileState((prevState) => ({ ...prevState, nationalities: data }));
  };

  const hasFieldChanged = (fieldName: keyof IProfileState, profileFieldName?: string) => {
    const stateValue = profileState[fieldName] || '';
    const profileValue =
      (memberInfo && (memberInfo as unknown as Record<string, unknown>)[profileFieldName || (fieldName as string)]) ||
      '';

    if (typeof stateValue === 'object' && stateValue !== null) {
      return (stateValue as IOption).value !== profileValue;
    }

    return stateValue !== profileValue;
  };

  const rowClass = 'flex flex-wrap md:gap-[24px]';
  const colMd3Class = 'w-full md:w-1/3 lg:w-1/4';

  return (
    <AsyncDataHandler isLoading={!displayUpdateButton}>
      <div className={styles['basic-information-container']}>
        {/* Prefix */}
        <div className={rowClass}>
          <div className={classNames(styles['profile-field'], colMd3Class)}>
            <Select
              label="Prefix"
              showClear={!!profileState.prefix}
              options={prefixOptions}
              selectedOption={profileState.prefix as IOption}
              hasChanged={isFetchedPrefix && hasFieldChanged('prefix')}
              onChange={handleDropdownChange('prefix')}
              disabled={!editMode}
              selectChangedStyle={SelectChangeVariant.Secondary}
            />
          </div>
        </div>

        {/* Name fields */}
        <div className={rowClass}>
          <div className={classNames(styles['profile-field'], colMd3Class)}>
            <Input
              label="First Name"
              required={true}
              value={profileState.firstName}
              hasChanged={hasFieldChanged('firstName')}
              onChange={handleInputChange('firstName')}
              disabled={!editMode}
              inputChangedStyle={InputChangeVariant.Secondary}
            />
          </div>
          <div className={classNames(styles['profile-field'], colMd3Class)}>
            <Input
              label="Middle Name"
              value={profileState.middleName}
              hasChanged={hasFieldChanged('middleName')}
              onChange={handleInputChange('middleName')}
              disabled={!editMode}
              inputChangedStyle={InputChangeVariant.Secondary}
            />
          </div>
          <div className={classNames(styles['profile-field'], colMd3Class)}>
            <Input
              label="Last Name"
              required={true}
              value={profileState.lastName}
              hasChanged={hasFieldChanged('lastName')}
              onChange={handleInputChange('lastName')}
              disabled={!editMode}
              inputChangedStyle={InputChangeVariant.Secondary}
            />
          </div>
        </div>

        {!selectedProfile?.isPrincipal && (
          <div className={rowClass}>
            <div className={classNames(styles['profile-field'], colMd3Class)}>
              <Select
                label="Relationship"
                options={relationshipOptions}
                selectedOption={profileState.relationship}
                hasChanged={isFetchedRelatioships && hasFieldChanged('relationship')}
                onChange={handleDropdownChange('relationship')}
                disabled={!editMode}
                selectChangedStyle={SelectChangeVariant.Secondary}
                required
              />
            </div>
          </div>
        )}

        {/* Additional name fields */}
        <div className={rowClass}>
          <div className={classNames(styles['profile-field'], colMd3Class)}>
            <Input
              label="Preferred Name"
              value={profileState.preferredName}
              hasChanged={hasFieldChanged('preferredName')}
              onChange={handleInputChange('preferredName')}
              disabled={!editMode}
              inputChangedStyle={InputChangeVariant.Secondary}
            />
          </div>
          <div className={classNames(styles['profile-field'], colMd3Class)}>
            <Input
              label="Maiden/Other Name"
              value={profileState.otherName}
              hasChanged={hasFieldChanged('otherName')}
              onChange={handleInputChange('otherName')}
              disabled={!editMode}
              inputChangedStyle={InputChangeVariant.Secondary}
            />
          </div>
        </div>

        {/* Gender and Status */}
        <div className={rowClass}>
          <div className={classNames(styles['profile-field'], colMd3Class)}>
            <Select
              label="Gender"
              showClear={!!profileState.gender}
              options={genderOptions}
              selectedOption={profileState.gender as IOption}
              hasChanged={isFetchedGenders && hasFieldChanged('gender')}
              onChange={handleDropdownChange('gender')}
              disabled={!editMode}
              selectChangedStyle={SelectChangeVariant.Secondary}
            />
          </div>
          <div className={classNames(styles['profile-field'], colMd3Class)}>
            <Select
              label="Relationship Status"
              showClear={!!profileState.maritalStatus}
              options={maritalStatusOptions}
              selectedOption={profileState.maritalStatus as IOption}
              hasChanged={isFetchedMaritalStatues && hasFieldChanged('maritalStatus')}
              onChange={handleDropdownChange('maritalStatus')}
              disabled={!editMode}
              selectChangedStyle={SelectChangeVariant.Secondary}
            />
          </div>
        </div>

        {/* Date of Birth */}
        <div className={rowClass}>
          <div className={classNames(styles['profile-field'], colMd3Class)}>
            <DatePicker
              value={profileState.dob}
              maxDate={new Date()}
              label="Date of Birth"
              hasChanged={hasFieldChanged('dob')}
              onChange={handleDateChange}
              disabled={!editMode}
              datePickerChangedStyle={DatePickerChangeVariant.Secondary}
              dateFormat={dateFormat}
            />
          </div>
        </div>

        {/* Birth Location */}
        <div className={rowClass}>
          <div className={classNames(styles['profile-field'], colMd3Class)}>
            <Select
              label="Country/Location of Birth"
              searchable={true}
              options={countryOptions}
              selectedOption={profileState.cob as IOption}
              hasChanged={isFetchedCountries && hasFieldChanged('cob')}
              onChange={(option) => {
                handleDropdownChange('cob')(option);
                handleDropdownChange('stateProvinceOfBirth')(null);
              }}
              disabled={!editMode}
              selectChangedStyle={SelectChangeVariant.Secondary}
            />
          </div>
          <div className={classNames(styles['profile-field'], colMd3Class)}>
            {states && states.length > 0 ? (
              <Select
                label="State/Province of Birth"
                searchable={true}
                showClear={!!profileState.stateProvinceOfBirth}
                options={stateOptions}
                selectedOption={{
                  label: profileState.stateProvinceOfBirth,
                  value: profileState.stateProvinceOfBirth,
                }}
                hasChanged={isFetchedStates && hasFieldChanged('stateProvinceOfBirth')}
                onChange={(data) =>
                  handleInputChange('stateProvinceOfBirth')({
                    target: { value: (data?.value as string) || '' },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                disabled={!editMode}
                selectChangedStyle={SelectChangeVariant.Secondary}
              />
            ) : (
              <Input
                label="State/Province of Birth"
                value={profileState.stateProvinceOfBirth}
                hasChanged={hasFieldChanged('stateProvinceOfBirth')}
                onChange={handleInputChange('stateProvinceOfBirth')}
                disabled={!editMode}
                inputChangedStyle={InputChangeVariant.Secondary}
              />
            )}
          </div>
          <div className={classNames(styles['profile-field'], colMd3Class)}>
            <Input
              label="City of Birth"
              value={profileState.cityOfBirth}
              hasChanged={hasFieldChanged('cityOfBirth')}
              onChange={handleInputChange('cityOfBirth')}
              disabled={!editMode}
              inputChangedStyle={InputChangeVariant.Secondary}
            />
          </div>
        </div>

        {/* Nationalities */}
        <div className={rowClass}>
          <div className={classNames(styles['profile-field'], colMd3Class)}>
            <label>Citizenship/Location</label>
            <NationalityList
              refNationalities={memberInfo?.nationalities || []}
              nationalities={profileState.nationalities}
              countries={countryOptions}
              onUpdate={handleNationalitiesUpdate}
            />
          </div>
        </div>
      </div>
    </AsyncDataHandler>
  );
};

export default BasicInformation;
